// useFacilityInfoData — fetches every table the Info tab renders, in parallel.
//
// Defensive contract: each table's result is returned with its own data + error
// so a failure on (e.g.) facility_access never blocks rendering of the rest of
// the page. The existing implementation in facility-detail.tsx threw on the
// first error and surfaced "Couldn't load Info" — that behaviour is gone.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type { Facility } from '../../../shared/types';
import { catalogLabel } from '../../../shared/service-roles';
import { useReader } from '../../../shared/save-path';

export interface FacilityContactRow {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  job_title: string | null;
  mobile: string | null;
  email: string | null;
  contact_role: string | null;
  scope_type: 'Facility' | 'Account' | string | null;
  preferred_contact_method: string | null;
  facility_id: string | null;
  account_id: string | null;
  // 2026-06-09 — Model B: these rows are now projected from the
  // contact_assignments junction (person-master + assignment). The fields
  // above describe the person (contacts master) with per-assignment phone/
  // email/role/title overrides folded in. The fields below describe the
  // displayed assignment so the card + POC manager can act on it.
  assignment_id: string | null;
  is_displayed: boolean;
  display_order: number | null;
  is_primary: boolean;
}

export interface FacilityAccessRow {
  access_id: string;
  facility_id: string;
  alarm_code: string | null;
  arm_disarm_procedure: string | null;
  monitoring_company: string | null;
  monitoring_phone: string | null;
  lock_type: string | null;
  lock_code: string | null;
  key_location: string | null;
  badge_issuer: string | null;
  badge_obtain_procedure: string | null;
  other_access_notes: string | null;
  site_cheat_sheet: string | null;
}

export interface FacilityDetailsRow {
  facility_id: string;
  total_sqft: number | null;
  // Plus type-specific fields below — surface as Record so we can render
  // whatever the detail table holds without hard-coding every column here.
  [key: string]: unknown;
}

/** A numeric column as a number, or null. Never 0 for a missing value. */
function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export interface ServiceAssignmentRow {
  assignment_id: string;
  facility_id: string | null;
  provider_id: string | null;
  provider_name: string | null;
  service_code: string | null;
  service_label: string | null;
  billing_method: string | null;
  billing_rate: number | null;
  invoice_to: string | null;
  sub_rate_monthly: number | null;
  /** 'hourly' or 'monthly'. On an hourly line the hourly rate IS what the sub is
   *  paid and sub_rate_monthly is only its rollup (blank with no set hours). */
  sub_rate_type: string | null;
  sub_hourly_rate: number | null;
  sub_hours_per_day: number | null;
  service_days_per_week: number | null;
  assignment_status: string | null;
  /** First night. Set and in the future on a Scheduled line — the card says so
   *  rather than showing an agreed line as though it were already running. */
  start_date: string | null;
  worker_name: string | null;
  worker_phone: string | null;
  training_status: string | null;
  training_completion_date: string | null;
  facility_access_issued: string | null;
  no_misses_bonus_enabled: boolean;
  no_misses_bonus_amount: number | null;
  no_misses_bonus_basis: string | null;
  or_count: number | null;
  pr_count: number | null;
  compliment_incentive_enabled: boolean;
  crew_size: number | null;
  notes: string | null;
  sub_agreement_url: string | null;
}

/**
 * 2026-05-26 — single row from `v_facility_margins` for this facility.
 * Returned by the same Promise.allSettled fan-out. NULL on fetch error or
 * if the view doesn't have a row for this facility_id. Used by
 * `FinancialSummaryCard` to detect billing-cluster membership and branch:
 *   - is_billing_primary=true + cluster_member_count <=1 → solo (Branch A)
 *   - is_billing_primary=true + cluster_member_count > 1 → cluster primary (Branch B)
 *   - is_billing_primary=false → bundled service location (Branch C)
 */
export interface FacilityMarginRow {
  facility_id: string;
  is_billing_primary: boolean;
  billing_account_id: string | null;
  /**
   * CW-Q Phase 6 — 'monthly_flat' | 'hourly' | 'unrated'. Present in the view
   * since CW-K but never read, which is why 14 hourly hospitals rendered the
   * same "(not set)" as a genuine data gap. Normalize through
   * `normalizeRateBasis` before use; do not compare the raw string.
   */
  rate_basis: string | null;
  /**
   * The view's copy of facilities.monthly_rate. Read alongside rate_basis so
   * the basis and the figure come from the same snapshot — deciding the basis
   * from the view and the amount from a separately-fetched facilities row is
   * how a "monthly_flat with no rate" contradiction gets on screen.
   */
  facility_monthly_rate: number | null;
  labor_margin_monthly: number | null;
  labor_margin_pct: number | null;
  data_quality_status: string | null;
  cluster_primary_facility_id: string | null;
  cluster_primary_facility_name: string | null;
  cluster_member_count: number | null;
  cluster_total_client_rate: number | null;
  cluster_total_sub_cost: number | null;
  cluster_margin_monthly: number | null;
  cluster_margin_pct: number | null;
}

/**
 * 2026-05-26 — single row from `v_billing_cluster_margins` when this facility
 * IS the cluster primary (filter: primary_facility_id = facility.facility_id).
 * Returns NULL for solo facilities and for non-primary members. Used to render
 * the "Combined cluster" subsection on the primary's FinancialSummaryCard
 * (member chip list + cluster aggregate totals).
 */
export interface BillingClusterRow {
  billing_account_id: string | null;
  billing_account_name: string | null;
  cluster_member_count: number | null;
  primary_member_count: number | null;
  bundled_member_count: number | null;
  primary_facility_id: string | null;
  primary_facility_name: string | null;
  member_facility_ids: string[] | null;
  member_facility_names: string[] | null;
  cluster_total_client_rate_monthly: number | null;
  cluster_total_sub_cost_monthly: number | null;
  cluster_active_assignment_count: number | null;
  cluster_margin_monthly: number | null;
  cluster_margin_pct: number | null;
  data_quality_status: string | null;
}

export interface FacilityInfoData {
  facility: Facility;
  details: FacilityDetailsRow | null;
  access: FacilityAccessRow | null;
  contacts: FacilityContactRow[];
  assignments: ServiceAssignmentRow[];
  /**
   * 2026-05-26 — margin row for this facility (cluster awareness lives here).
   */
  margin: FacilityMarginRow | null;
  /**
   * 2026-05-26 — cluster aggregate row when this facility IS the primary in a
   * multi-member billing cluster; NULL otherwise.
   */
  cluster: BillingClusterRow | null;
  /**
   * 2026-05-22 — field_path values from public.facility_field_na for this facility.
   * Consumed by FacilityNaProvider so InlineEditField rows can self-hide in
   * read mode (when hideBlanks=true) and display an "N/A" pill in edit mode.
   */
  naFieldPaths: string[];
  // Per-table errors — caller can render an inline banner per section
  // instead of aborting the whole page.
  errors: {
    details: string | null;
    access: string | null;
    contacts: string | null;
    assignments: string | null;
    margin: string | null;
    cluster: string | null;
  };
  loading: boolean;
  refresh: () => void;
}

// 2026-09-17 — detailsTableFor() is gone, and so is the thing it existed for.
//
// It used to mirror public.facility_detail_table_for() and route a building's
// facility_type to one of three detail tables. That routing was the cause of
// four live defects, and every one of them was the same shape: a type that
// matched no arm, so a building's details went somewhere nothing read, or
// nowhere at all. There is now ONE table, public.facility_details, holding
// every column any type can record. Nothing branches; nothing can fall through.
//
// Which of those columns a given type is ASKED for is a display decision, and
// it lives in DETAIL_FIELD_SPECS in facility-detail.tsx, keyed on facility_type.
// ⛔ That map is now the only guard. The database will no longer refuse a
// hospital column on a dialysis row, because they are the same row.

export function useFacilityInfoData(facility: Facility): FacilityInfoData {
  const [details, setDetails] = useState<FacilityDetailsRow | null>(null);
  const [access, setAccess] = useState<FacilityAccessRow | null>(null);
  const [contacts, setContacts] = useState<FacilityContactRow[]>([]);
  const [assignments, setAssignments] = useState<ServiceAssignmentRow[]>([]);
  const [margin, setMargin] = useState<FacilityMarginRow | null>(null);
  const [cluster, setCluster] = useState<BillingClusterRow | null>(null);
  const [naFieldPaths, setNaFieldPaths] = useState<string[]>([]);
  const [errors, setErrors] = useState({
    details: null as string | null,
    access: null as string | null,
    contacts: null as string | null,
    assignments: null as string | null,
    margin: null as string | null,
    cluster: null as string | null,
  });
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  // ⛔ THIS LOADER NOW SAYS WHAT IT READS — 2026-09-16 (info-tab-one-edit-door).
  //
  // Found by smoke test: saving a contact's job title wrote it to the database
  // and the card went on showing the old one. The write declared
  // `touches: ['contacts']` and there was nobody registered to hear it, so a
  // saved value appeared only where a card had been handed the right callback
  // by hand.
  //
  // ⛔ REGISTER THE JOINED TABLES TOO (PLAYBOOK §15.2). This loader reaches a
  // person through `contact_assignments` and a subcontractor's name through
  // `service_providers`; a card showing a person's phone number is stale when
  // the PERSON changes, not only when the assignment does. `facilities` is here
  // because a service line's rate is recomputed onto the building by a trigger,
  // so the money on this tab moves when that row does.
  //
  // Safe by construction: refresh() bumps a key and the loader keeps the
  // current data on screen while it re-reads — it does not flip `loading`, so
  // nothing unmounts a card mid-save (the CW-O lesson immediately below).
  useReader(
    [
      'facility_access',
      'facility_details',
      'contacts',
      'contact_assignments',
      'service_assignments',
      'service_providers',
      'facilities',
      'facility_field_na',
    ],
    refresh,
  );

  // 2026-08-13 (CW-O PR4) — a refetch must NOT flip `loading` back to true.
  //
  // FacilityInfoTab renders a skeleton whenever `loading` is true, which
  // UNMOUNTS every card. saveDetailsField() calls refresh() after each
  // successful write, so a card save that committed one field and failed
  // another had its own failure banner — and its edit mode, and the failed
  // field's typed value — destroyed by the remount that the SUCCESS triggered.
  // The card came back in view mode looking like a clean save. Proven on
  // FAC-0135 on 2026-08-13: one PATCH 200 and one PATCH 400 in the same save,
  // no banner. That is the CW-O bug inverted, and it silently defeated the
  // PR1 honesty fix in exactly the case PR1 exists for.
  //
  // So: the skeleton is for the FIRST load of a facility. A refetch keeps the
  // current data on screen while it runs.
  const lastFacilityRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const isNewFacility = lastFacilityRef.current !== facility.facility_id;
      lastFacilityRef.current = facility.facility_id;
      if (isNewFacility) setLoading(true);

      // Build a settled-promise array — every query is independent and we
      // never let one failure cascade. `Promise.allSettled` would also work;
      // we mirror that semantics here with per-query try/catch via maybeSingle.
      const accessP = supabase
        .from('facility_access')
        .select(
          'access_id, facility_id, alarm_code, arm_disarm_procedure, monitoring_company, monitoring_phone, lock_type, lock_code, key_location, badge_issuer, badge_obtain_procedure, other_access_notes, site_cheat_sheet',
        )
        .eq('facility_id', facility.facility_id)
        .maybeSingle();

      // One table, every type. No branch, nothing to fall through.
      const detailsP = supabase
        .from('facility_details')
        .select('*')
        .eq('facility_id', facility.facility_id)
        .maybeSingle();

      // 2026-06-09 — Model B: displayed contacts now come from the
      // contact_assignments junction (joined to the contacts person-master
      // below via a flat IN-lookup — no PostgREST embed, per the flat-fetch
      // memo). Three-tier display set, mirroring the legacy contacts filter:
      //   1+2. assignments pinned to this facility (facility_id = X)
      //   3.   account-wide assignments (facility_id IS NULL AND account_id = this account)
      // Only is_displayed=true rows render on the card; the POC manager loads
      // the full set (displayed + hidden) separately.
      const contactsP = supabase
        .from('contact_assignments')
        .select(
          'assignment_id, contact_id, account_id, facility_id, contact_role, job_title, assignment_email, assignment_mobile, is_displayed, display_order, is_primary',
        )
        .eq('is_displayed', true)
        .or(
          `facility_id.eq.${facility.facility_id},` +
            `and(facility_id.is.null,account_id.eq.${facility.account_id ?? '__none__'})`,
        )
        .order('display_order', { ascending: true, nullsFirst: false });

      // Service assignments — flat select; partner_name is hydrated client-side
      // below. No PostgREST embed → resilient to FK / schema-cache drift. See
      // docs/database-changes.md.
      const assignmentsP = supabase
        .from('service_assignments')
        .select(
          `assignment_id, facility_id, provider_id, service_code, service_catalog(display_name), billing_method, billing_rate, invoice_to, sub_rate_monthly, sub_rate_type, sub_hourly_rate, sub_hours_per_day, service_days_per_week, assignment_status, start_date, worker_name, worker_phone, training_status, training_completion_date, facility_access_issued, no_misses_bonus_enabled, no_misses_bonus_amount, no_misses_bonus_basis, or_count, pr_count, compliment_incentive_enabled, crew_size, notes, sub_agreement_url`,
        )
        .eq('facility_id', facility.facility_id)
        // ⛔ ACTIVE, SCHEDULED, **AND SUSPENDED**. A line that starts next month
        // is a real, agreed line — it is what somebody negotiated — and hiding
        // it makes the building read "no active service assignments on file"
        // when two lines worth $23,512.84/mo are sitting right there.
        //
        // Ben, 2026-09-11, on Houston Physicians: "I distantly remember making
        // two service lines. They are not currently visible in the account card.
        // Why? Where did they go?" They were Scheduled: the building starts
        // 1 October, and a trigger derives the status from the dates.
        //
        // This is fallout from the building now existing from the card's first
        // save. Before that a building appeared at go-live and its lines were
        // Active immediately, so Active-only never hid anything. Only THREE rows
        // in the whole database are Scheduled today — all of them on buildings
        // created since that change — which is why nobody hit it before.
        //
        // Item 904: a paused line is the same kind of omission. Jose paused
        // Omaha West for flood damage and the card said "No service lines on
        // file" and "$0" sub cost for a line that is still owed $2,360/mo on
        // resume — the figure was never lost, the card just stopped showing it.
        // TCSD-0077: a paused building is left out of the totals but kept on
        // the screen, marked, and is never filtered off a screen.
        //
        // ⛔ THE MONEY STAYS ACTIVE-ONLY. Sub cost, margin and the billing
        // rollup must not count a line that has not started or is paused; the
        // finance tie-out balances to the cent on that basis. The list SHOWS
        // the line and says when it starts, or that it is paused; the totals
        // still ignore it.
        .in('assignment_status', ['Active', 'Scheduled', 'Suspended']);

      // 2026-05-26 — Billing cluster awareness. Two parallel reads:
      //   marginP  → v_facility_margins row for this facility (1:1; always
      //              try). Powers Branch C (bundled banner) and Branch B
      //              trigger (is_billing_primary + cluster_member_count>1).
      //   clusterP → v_billing_cluster_margins row WHERE primary_facility_id =
      //              this facility. Returns NULL for solo + non-primary;
      //              gives the chip list + cluster aggregates when this
      //              facility IS the primary of a multi-member cluster.
      // Both maybeSingle so a fetch failure / no-row doesn't throw — we
      // fall through to the legacy standalone rendering.
      const marginP = supabase
        .from('v_facility_margins')
        .select(
          // ONE literal string, on one line, on purpose. supabase-js infers the
          // row type from the select at the type level; a concatenated or
          // multi-line-joined select widens to `string` and every column comes
          // back as GenericStringError. Add columns inside these quotes.
          'facility_id, is_billing_primary, billing_account_id, rate_basis, facility_monthly_rate, labor_margin_monthly, labor_margin_pct, data_quality_status, cluster_primary_facility_id, cluster_primary_facility_name, cluster_member_count, cluster_total_client_rate, cluster_total_sub_cost, cluster_margin_monthly, cluster_margin_pct',
        )
        .eq('facility_id', facility.facility_id)
        .maybeSingle();

      const clusterP = supabase
        .from('v_billing_cluster_margins')
        .select(
          'billing_account_id, billing_account_name, cluster_member_count, primary_member_count, bundled_member_count, primary_facility_id, primary_facility_name, member_facility_ids, member_facility_names, cluster_total_client_rate_monthly, cluster_total_sub_cost_monthly, cluster_active_assignment_count, cluster_margin_monthly, cluster_margin_pct, data_quality_status',
        )
        .eq('primary_facility_id', facility.facility_id)
        .maybeSingle();

      const [accessRes, detailsRes, contactsRes, assignmentsRes, marginRes, clusterRes] =
        await Promise.all([accessP, detailsP, contactsP, assignmentsP, marginP, clusterP]);

      if (cancelled) return;

      // Access
      if (accessRes.error) {
        setErrors((e) => ({ ...e, access: accessRes.error?.message ?? 'Failed to load access' }));
        setAccess(null);
      } else {
        setErrors((e) => ({ ...e, access: null }));
        setAccess((accessRes.data ?? null) as FacilityAccessRow | null);
      }

      // Details
      if (detailsRes.error) {
        setErrors((e) => ({ ...e, details: detailsRes.error?.message ?? 'Failed to load details' }));
        setDetails(null);
      } else {
        setErrors((e) => ({ ...e, details: null }));
        setDetails((detailsRes.data ?? null) as FacilityDetailsRow | null);
      }

      // Contacts (Model B) — hydrate the person-master via an IN-lookup on
      // contacts (flat-fetch, no embed) then client-join. Per-assignment
      // phone/email/role/title override the master's defaults.
      if (contactsRes.error) {
        setErrors((e) => ({ ...e, contacts: contactsRes.error?.message ?? 'Failed to load contacts' }));
        setContacts([]);
      } else {
        setErrors((e) => ({ ...e, contacts: null }));
        const aRows = (contactsRes.data ?? []) as Array<Record<string, unknown>>;
        const contactIds = Array.from(
          new Set(aRows.map((r) => r.contact_id).filter((v): v is string => typeof v === 'string' && Boolean(v))),
        );
        const masterRes = contactIds.length === 0
          ? { data: [] as unknown[], error: null }
          : await supabase
              .from('contacts')
              .select(
                'contact_id, first_name, last_name, preferred_name, job_title, mobile, email, contact_role, scope_type, preferred_contact_method, account_id, facility_id',
              )
              .in('contact_id', contactIds);
        if (cancelled) return;
        const mMap: Record<string, Record<string, unknown>> = {};
        for (const m of (masterRes.data ?? []) as Array<Record<string, unknown>>) {
          mMap[m.contact_id as string] = m;
        }
        const rows: FacilityContactRow[] = aRows.map((a) => {
          const m = mMap[a.contact_id as string] ?? {};
          return {
            contact_id: a.contact_id as string,
            first_name: (m.first_name as string | null) ?? null,
            last_name: (m.last_name as string | null) ?? null,
            preferred_name: (m.preferred_name as string | null) ?? null,
            job_title: (a.job_title as string | null) ?? (m.job_title as string | null) ?? null,
            mobile: (a.assignment_mobile as string | null) ?? (m.mobile as string | null) ?? null,
            email: (a.assignment_email as string | null) ?? (m.email as string | null) ?? null,
            contact_role: (a.contact_role as string | null) ?? (m.contact_role as string | null) ?? null,
            scope_type: (m.scope_type as string | null) ?? ((a.facility_id as string | null) ? 'Facility' : 'Account'),
            preferred_contact_method: (m.preferred_contact_method as string | null) ?? null,
            facility_id: (a.facility_id as string | null) ?? null,
            account_id: (a.account_id as string | null) ?? (m.account_id as string | null) ?? null,
            assignment_id: (a.assignment_id as string | null) ?? null,
            is_displayed: (a.is_displayed as boolean | null) ?? true,
            display_order: (a.display_order as number | null) ?? null,
            is_primary: (a.is_primary as boolean | null) ?? false,
          };
        });
        setContacts(rows);
      }

      // Assignments — hydrate provider_name via a separate IN-lookup on
      // service_providers (no PostgREST embed). Survives missing-FK and
      // stale-schema-cache scenarios.
      if (assignmentsRes.error) {
        setErrors((e) => ({ ...e, assignments: assignmentsRes.error?.message ?? 'Failed to load assignments' }));
        setAssignments([]);
      } else {
        setErrors((e) => ({ ...e, assignments: null }));
        const flatList = (assignmentsRes.data ?? []) as Array<Record<string, unknown>>;
        const providerIds = Array.from(
          new Set(flatList.map((r) => r.provider_id).filter((v): v is string => typeof v === 'string' && Boolean(v))),
        );
        const provRes = providerIds.length === 0
          ? { data: [] as unknown[], error: null }
          : await supabase.from('service_providers').select('provider_id, partner_name').in('provider_id', providerIds);
        if (cancelled) return;
        const provMap: Record<string, string | null> = {};
        for (const p of (provRes.data ?? []) as Array<{ provider_id: string; partner_name: string | null }>) {
          provMap[p.provider_id] = p.partner_name;
        }
        const rows = flatList.map((row) => ({
          assignment_id: row.assignment_id as string,
          facility_id: row.facility_id as string,
          provider_id: (row.provider_id as string | null) ?? null,
          provider_name: typeof row.provider_id === 'string' && row.provider_id ? provMap[row.provider_id] ?? null : null,
          service_code: row.service_code as string | null,
          service_label: catalogLabel(
            row.service_catalog as Parameters<typeof catalogLabel>[0],
          ),

          billing_method: row.billing_method as string | null,
          billing_rate: row.billing_rate as number | null,
          invoice_to: row.invoice_to as string | null,
          sub_rate_monthly: row.sub_rate_monthly as number | null,
          sub_rate_type: (row.sub_rate_type as string | null) ?? null,
          sub_hourly_rate: num(row.sub_hourly_rate),
          sub_hours_per_day: num(row.sub_hours_per_day),
          service_days_per_week: num(row.service_days_per_week),
          assignment_status: row.assignment_status as string | null,
          start_date: (row.start_date as string | null) ?? null,
          worker_name: row.worker_name as string | null,
          worker_phone: row.worker_phone as string | null,
          training_status: row.training_status as string | null,
          training_completion_date: row.training_completion_date as string | null,
          facility_access_issued: row.facility_access_issued as string | null,
          no_misses_bonus_enabled: (row.no_misses_bonus_enabled as boolean | null) ?? false,
          no_misses_bonus_amount: row.no_misses_bonus_amount as number | null,
          no_misses_bonus_basis: row.no_misses_bonus_basis as string | null,
          or_count: row.or_count as number | null,
          pr_count: row.pr_count as number | null,
          compliment_incentive_enabled: (row.compliment_incentive_enabled as boolean | null) ?? false,
          crew_size: row.crew_size as number | null,
          notes: row.notes as string | null,
          sub_agreement_url: row.sub_agreement_url as string | null,
        })) as ServiceAssignmentRow[];
        setAssignments(rows);
      }

      // 2026-05-26 — margin (v_facility_margins). Defensive: on error or no
      // row, leave margin=null and FinancialSummaryCard falls through to
      // the legacy standalone rendering as if cluster columns didn't exist.
      if (marginRes.error) {
        setErrors((e) => ({ ...e, margin: marginRes.error?.message ?? 'Failed to load margin' }));
        setMargin(null);
      } else {
        setErrors((e) => ({ ...e, margin: null }));
        setMargin((marginRes.data ?? null) as FacilityMarginRow | null);
      }

      // 2026-05-26 — cluster aggregate (v_billing_cluster_margins). Only
      // returns a row when this facility IS the cluster primary; NULL for
      // solo + non-primary. Card's Branch B subsection uses the chip list.
      if (clusterRes.error) {
        setErrors((e) => ({ ...e, cluster: clusterRes.error?.message ?? 'Failed to load cluster' }));
        setCluster(null);
      } else {
        setErrors((e) => ({ ...e, cluster: null }));
        setCluster((clusterRes.data ?? null) as BillingClusterRow | null);
      }

      // 2026-05-22 — facility_field_na markers (AM Collect auto-promotion).
      // Empty result on error is acceptable: the read-path falls back to
      // treating all fields as not-NA (same as pre-2026-05-22 behavior).
      try {
        const { data: naRows } = await supabase
          .from('facility_field_na')
          .select('field_path')
          .eq('facility_id', facility.facility_id);
        if (!cancelled) {
          setNaFieldPaths(((naRows ?? []) as Array<{ field_path: string }>)
            .map((r) => r.field_path));
        }
      } catch {
        if (!cancelled) setNaFieldPaths([]);
      }

      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facility.facility_id, facility.account_id, reloadKey]);

  return {
    facility,
    details,
    access,
    contacts,
    assignments,
    margin,
    cluster,
    naFieldPaths,
    errors,
    loading,
    refresh,
  };
}
